import { useState } from 'react';
import { toast } from 'sonner';
import { Share2, Copy, MessageCircle, Send, X, Mail, Check } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Universal share button — works for any URL-addressable content
 * (courses, live classes, audio rooms, exams, ebooks, past papers, etc.).
 *
 * Props
 *   title        – string shown in the share text ("Newton's Laws of Motion")
 *   path         – relative path on this site, e.g. "/courses/abc123"
 *   url          – full URL (overrides `path`); use when sharing an external resource
 *   prefix       – kind label that prefixes the share message, e.g. "Live class"
 *   variant      – "icon" | "icon-text" | "button" (defaults to "icon")
 *   size         – button size (passed to shadcn Button)
 *   className    – wrapper className
 *   testId       – data-testid prefix (default "share")
 */
export default function ShareButton({
  title = '',
  path = '',
  url: explicitUrl = '',
  prefix = 'Check out',
  variant = 'icon',
  size = 'icon',
  className = '',
  testId = 'share',
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrl = explicitUrl || (path ? `${origin}${path}` : origin);
  const cleanTitle = (title || '').replace(/\s+/g, ' ').trim();
  const text = cleanTitle ? `${prefix}: "${cleanTitle}" on Darasani` : `${prefix} on Darasani`;
  const fullMsg = `${text}\n${shareUrl}`;

  // Encoders for sharer URLs
  const encoded = encodeURIComponent(fullMsg);
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(text);

  const open_ = (e) => {
    if (e) e.stopPropagation();
    // Prefer the native share sheet on supported devices (mobile / PWA)
    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator
        .share({ title: cleanTitle || 'Darasani', text, url: shareUrl })
        .catch(() => setOpen(true));
      return;
    }
    setOpen(true);
  };

  const close = (e) => {
    if (e) e.stopPropagation();
    setOpen(false);
    setCopied(false);
  };

  const copyLink = async (e) => {
    if (e) e.stopPropagation();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  // Trigger button shape
  const triggerProps = {
    onClick: open_,
    title: 'Share',
    'aria-label': 'Share',
    'data-testid': `${testId}-trigger`,
  };
  let trigger;
  if (variant === 'button') {
    trigger = (
      <Button size={size} variant="outline" className={`gap-2 ${className}`} {...triggerProps}>
        <Share2 className="w-4 h-4" /> Share
      </Button>
    );
  } else if (variant === 'icon-text') {
    trigger = (
      <button
        type="button"
        className={`inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-violet-700 transition ${className}`}
        {...triggerProps}
      >
        <Share2 className="w-4 h-4" /> Share
      </button>
    );
  } else {
    trigger = (
      <button
        type="button"
        className={`h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/90 hover:bg-white border border-slate-200 text-slate-600 hover:text-violet-700 shadow-sm transition ${className}`}
        {...triggerProps}
      >
        <Share2 className="w-4 h-4" />
      </button>
    );
  }

  return (
    <>
      {trigger}

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={close}
          data-testid={`${testId}-sheet`}
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="font-semibold text-slate-900">Share</div>
              <button onClick={close} className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-slate-100" data-testid={`${testId}-close`}>
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Quick targets */}
              <div className="grid grid-cols-4 gap-2 text-center">
                <a
                  href={`https://wa.me/?text=${encoded}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-emerald-50 transition"
                  data-testid={`${testId}-whatsapp`}
                >
                  <span className="h-11 w-11 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center">
                    <MessageCircle className="w-5 h-5" fill="currentColor" />
                  </span>
                  <span className="text-[11px] text-slate-700">WhatsApp</span>
                </a>
                <a
                  href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-sky-50 transition"
                  data-testid={`${testId}-telegram`}
                >
                  <span className="h-11 w-11 rounded-full bg-sky-500 text-white inline-flex items-center justify-center">
                    <Send className="w-5 h-5" />
                  </span>
                  <span className="text-[11px] text-slate-700">Telegram</span>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encoded}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-slate-100 transition"
                  data-testid={`${testId}-twitter`}
                >
                  <span className="h-11 w-11 rounded-full bg-slate-900 text-white inline-flex items-center justify-center font-bold">
                    X
                  </span>
                  <span className="text-[11px] text-slate-700">X (Twitter)</span>
                </a>
                <a
                  href={`mailto:?subject=${encodedTitle}&body=${encoded}`}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-amber-50 transition"
                  data-testid={`${testId}-email`}
                >
                  <span className="h-11 w-11 rounded-full bg-amber-500 text-white inline-flex items-center justify-center">
                    <Mail className="w-5 h-5" />
                  </span>
                  <span className="text-[11px] text-slate-700">Email</span>
                </a>
              </div>

              {/* Link row */}
              <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2">
                <input
                  type="text"
                  readOnly
                  value={shareUrl}
                  className="flex-1 bg-transparent text-xs text-slate-700 outline-none truncate"
                  onClick={(e) => e.currentTarget.select()}
                  data-testid={`${testId}-link-input`}
                />
                <button
                  onClick={copyLink}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-violet-600 text-white hover:bg-violet-700 transition"
                  data-testid={`${testId}-copy`}
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
